/**
 * Ties in a session's result (roadmap S-5, rulings R4 and R13, owner 2026-10-28).
 *
 * Items whose totals are equal are tied and share a rank (1, 2, 2, 4). A tie
 * is shown as a tie with the rule below; the session's facilitator may break
 * it with a reason, recorded as an override (`override_session_rank`), and a
 * tie left unbroken is published as a tie. The groups are worked out once,
 * when the answers are counted (`buildRankingOutputFromAggregates`), and saved
 * as `ranking_output.ties`; the server numbers the result rows from them and
 * takes an item out of its tie when its rank is overridden.
 *
 * A sorting method's result is groups, not an order, so it has no ties.
 */

/** Each group: two or more item ids, in the saved order, that share a rank. */
export type TieGroups = string[][];

/** The rule shown beside a result that has a tie. */
export const TIE_RULE =
  'Items with the same total share a rank. The facilitator may break a tie, with a reason; a tie left unbroken is published as a tie.';

/** Totals within this of each other are the same total (averages are floats). */
const SAME_TOTAL = 1e-9;

/** The ties in a ranked order: neighbours with the same total. */
export function findTies(
  rankedItemIds: readonly string[],
  scores: Readonly<Record<string, { total?: number } | undefined>>,
): TieGroups {
  const groups: TieGroups = [];
  let current: string[] = [];
  let currentTotal: number | undefined;
  for (const id of rankedItemIds) {
    const total = scores[id]?.total;
    if (
      typeof total === 'number' &&
      typeof currentTotal === 'number' &&
      Math.abs(total - currentTotal) <= SAME_TOTAL
    ) {
      current.push(id);
    } else {
      if (current.length > 1) groups.push(current);
      current = [id];
      currentTotal = typeof total === 'number' ? total : undefined;
    }
  }
  if (current.length > 1) groups.push(current);
  return groups;
}

/** Read `ranking_output.ties` as saved; anything malformed counts as no tie. */
export function readTies(value: unknown): TieGroups {
  if (!Array.isArray(value)) return [];
  return value
    .filter((g): g is unknown[] => Array.isArray(g))
    .map((g) => g.filter((id): id is string => typeof id === 'string'))
    .filter((g) => g.length > 1);
}

/**
 * Each item's rank, with tied items sharing the rank of the first of them.
 * `tied` says whether the item shares its rank.
 */
export function sharedRanks(
  rankedItemIds: readonly string[],
  ties: TieGroups | null | undefined,
): Record<string, { rank: number; tied: boolean }> {
  const groupOf = new Map<string, number>();
  (ties ?? []).forEach((group, index) => group.forEach((id) => groupOf.set(id, index)));
  const out: Record<string, { rank: number; tied: boolean }> = {};
  const groupRank = new Map<number, number>();
  rankedItemIds.forEach((id, index) => {
    const group = groupOf.get(id);
    if (group === undefined) {
      out[id] = { rank: index + 1, tied: false };
      return;
    }
    if (!groupRank.has(group)) groupRank.set(group, index + 1);
    out[id] = { rank: groupRank.get(group)!, tied: true };
  });
  return out;
}
